import { create } from 'apisauce'
import * as dot from 'dot-object'
import * as Cookie from 'js-cookie'
import { getAccessToken } from './Auth'

const LG_SANITIZE_METHODS = ['post', 'patch']
/**
 * サニタイズ用の変換パターン
 *
 * PHPのコードと合わせる必要がある
 * @see app/Http/Middleware/LeadGridSanitizeReverse.php
 */
const LG_SANITIZE_PATTERN = {
  ['$']: '[LEADGRID_SANITIZE_DOLLAR]',
  ['(']: '[LEADGRID_SANITIZE_OPEN_PARENTHESIS]',
  [')']: '[LEADGRID_SANITIZE_CLOSE_PARENTHESIS]',
  ['fsockopen']: '[LEADGRID_SANITIZE_FSOCKETOPEN]',
}

export const createAPI = (options?: object) => {
  const api = create({
    baseURL: '/api/',
    headers: {
      'X-XSRF-TOKEN': Cookie.get('XSRF-TOKEN'),
      Accept: 'application/json',
    },
    ...options,
  })

  api.addRequestTransform((request) => {
    const access_token = getAccessToken()

    if (access_token !== undefined && request.headers) {
      request.headers.Authorization = `Bearer ${access_token}`
    }

    if (request.data instanceof FormData && request.headers) {
      request.headers['Content-Type'] = 'multipart/form-data'
    }

    if (request.params) {
      request.params = getParams(request.params)
    }

    if (
      LG_SANITIZE_METHODS.includes(request.method ?? '') &&
      !(request.data instanceof FormData)
    ) {
      request.data = sanitize(request.data ?? {})
    } else if (
      LG_SANITIZE_METHODS.includes(request.method ?? '') &&
      request.data instanceof FormData
    ) {
      request.data = sanitizeFormData(request.data)
    }
  })

  api.addResponseTransform((response) => {
    if (!response.ok && response.data && response.data.errors) {
      response.data.errors = dot.object(response.data.errors)
      response.data.errors.status = response.status
    }
  })

  return api
}

//object型のパラメータをJSON文字列に変換する
const getParams = (params: any): any => {
  const transformedParams = {}
  for (const key in params) {
    if (params.hasOwnProperty(key)) {
      if (isParamTypeObject(params, key)) {
        transformedParams[key] = JSON.stringify(params[key])
      } else {
        transformedParams[key] = params[key]
      }
    }
  }
  return transformedParams
}

const isParamTypeObject = (params: any, key: string): boolean => {
  return (
    params[key] !== null &&
    typeof params[key] === 'object' &&
    !Array.isArray(params[key])
  )
}

export const api = createAPI()

export const loginAPI = create({
  baseURL: '/',
  headers: {
    'X-XSRF-TOKEN': Cookie.get('XSRF-TOKEN'),
    Accept: 'application/json',
  },
})

export const downloadApi = createAPI({ responseType: 'arraybuffer' })

/**
 * WAFでブロックされる文字列を置換する
 *
 * @see https://giginc.atlassian.net/browse/LEAD-4665?atlOrigin=eyJpIjoiOTdjMTdhOTgzNzBiNDk4MjkzMzk0YjYwNzUyMzYwMzIiLCJwIjoiaiJ9
 *
 * @param method HTTPメソッド
 * @param data
 * @returns
 */
function sanitize(data: { [key: string]: string | number | boolean }) {
  const result = { ...data }

  for (const key of Object.keys(result)) {
    const target = result[key]

    if (typeof target === 'string') {
      result[key] = Object.keys(LG_SANITIZE_PATTERN).reduce((acc, cur) => {
        // @ts-ignore tsconfigのlibをes2021にすればreplaceAllが使えるけど、影響が大きいので一旦ignore
        return acc.replaceAll(cur, LG_SANITIZE_PATTERN[cur])
      }, target)
    }
  }

  return result
}

function sanitizeString(input: string): string {
  return Object.entries(LG_SANITIZE_PATTERN).reduce((acc, [key, value]) => {
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    return acc.replace(new RegExp(escapedKey, 'g'), value)
  }, input)
}

/**
 * WAFでブロックされる文字列を置換する（FormData用）
 *
 * @see https://giginc.atlassian.net/browse/LEAD-4665?atlOrigin=eyJpIjoiOTdjMTdhOTgzNzBiNDk4MjkzMzk0YjYwNzUyMzYwMzIiLCJwIjoiaiJ9
 *
 * @param data
 * @returns
 */
function sanitizeFormData(formData: FormData): FormData {
  const sanitizedFormData = new FormData()

  formData.forEach((value, key) => {
    if (typeof value === 'string') {
      // 文字列の場合、サニタイズを適用
      sanitizedFormData.append(key, sanitizeString(value))
    } else {
      // その他の型の場合、そのまま追加
      sanitizedFormData.append(key, value)
    }
  })

  return sanitizedFormData
}
